/**
 * navigation/prepaidOrder.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Pay before signup — the platform side of the return from Cashfree.
 *
 * A visitor buys a plan on the softknack.com price page with no account. After
 * paying, Cashfree sends them here:
 *   ?mode=signup&product=<slug>&prepaid=<order id>&prepaid_token=<secret>
 *
 * This module keeps the order id and its claim secret from that URL until the
 * signup form is sent (the secret is what lets the new org claim the payment),
 * and takes them out of the address bar so they are not left in history or a
 * shared screenshot. Web-only; every export is a no-op on native.
 */
import { Platform } from 'react-native';

const STORAGE_KEY = 'sk_prepaid_order';
const ORDER_ID = /^pord_[0-9A-HJKMNP-TV-Z]{26}$/;
const TOKEN = /^[\w-]{16,128}$/;
const PARAM_KEYS = ['prepaid', 'prepaid_token'] as const;

export interface PrepaidClaim {
  orderId: string;
  token: string;
}

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

function valid(orderId: unknown, token: unknown): PrepaidClaim | null {
  return typeof orderId === 'string' && typeof token === 'string' && ORDER_ID.test(orderId) && TOKEN.test(token)
    ? { orderId, token }
    : null;
}

/** Call once on boot: keep a claim present in the URL, and strip it from the URL. */
export function capturePrepaidFromUrl(): void {
  if (!isWeb()) return;
  const q = new URLSearchParams(window.location.search);
  if (!PARAM_KEYS.some((k) => q.has(k))) return;
  const claim = valid(q.get('prepaid'), q.get('prepaid_token'));
  if (claim) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(claim));
    } catch {
      /* storage unavailable — the form still works, just without the claim */
    }
  }
  try {
    const url = new URL(window.location.href);
    PARAM_KEYS.forEach((k) => url.searchParams.delete(k));
    const query = url.searchParams.toString();
    window.history.replaceState({}, '', `${url.pathname}${query ? `?${query}` : ''}`);
  } catch {
    /* history API unavailable — non-fatal */
  }
}

/** The claim waiting for signup, if any. */
export function readPrepaidClaim(): PrepaidClaim | null {
  if (!isWeb()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return valid(parsed?.orderId, parsed?.token);
  } catch {
    return null;
  }
}

/** After a signup that sent the claim, or an order the API no longer knows. */
export function clearPrepaidClaim(): void {
  if (!isWeb()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}
